import "handsontable/dist/handsontable.full.min.css";
// import "../../Theme/CSS/audiotrackstable.css";
import { registerAllModules } from "handsontable/registry";
import { HotTable } from "@handsontable/react";
import SaveIcon from "@mui/icons-material/Save";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { Box, Button, useTheme } from "@mui/material";
import { registerRenderer, textRenderer } from "handsontable/renderers";
import { assetService, cacheService } from "../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../Core/Api/Types/update-metadata-fields-request";
import moment from "moment";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import React from "react";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import { useTranslation } from "react-i18next";

interface AudioTracksExcelProps {
  asset: DbAssetObjectInfos;
  onChange?: (metadata: IDbBaseMetadataValueInfos) => void;
}

const AudioTracksExcel: React.FC<AudioTracksExcelProps> = (props) => {
  const { t } = useTranslation();
  const metadataFromStorage: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const languages = metadataFromStorage.find((m) => m.tagName === "languageAudio");
  const languagesValues = languages?.values?.map((lang) => lang.value!)!;
  const allowEdition =
    UserHasAcl(AclTypes.aclSuperUser) ||
    (props.asset.classification === ObjectClass.Raw && UserHasAcl(AclTypes.aclMetadataAudioTracksUpdate));

  // Register handsontable
  registerAllModules();
  const theme = useTheme();

  registerRenderer("customStylesRenderer", (hotInstance, TD, ...rest) => {
    textRenderer(hotInstance, TD, ...rest);

    TD.style.fontWeight = "bold";
    TD.style.color = theme.palette.text.primary;
    TD.style.background = theme.palette.background.default;
  });

  const enqueueCustomSnackbar = useCustomSnackbar();

  // Make a copy of object to be able to modify it
  let metadata = GetMetadataByTagName(props.asset, "audioChannels");

  if (!metadata || !metadata.value) return <></>;

  let audioChannels: any[] = JSON.parse(JSON.stringify(metadata?.value));

  audioChannels.forEach((channel) => {
    if (channel.language === null)
      channel.language = {
        id: 0,
        externalId: 0,
        value: "",
        label: "",
        rfc5646: "",
      };
  });

  let r128 = audioChannels.some((channel) => channel.lra || channel.truePeak);

  let colHeaders = [
    "Id",
    t("common:version"),
    t("common:language"),
    t("common:position"),
    t("common:sampling"),
    t("common:bitDepth"),
    t("common:format"),
    "R128?",
    "Dolby-E?",
  ];
  if (r128) {
    colHeaders.push(t("common:loudness"));
    colHeaders.push("LRA");
    colHeaders.push(t("common:truePeaks"));
  }

  let columns = [
    { data: "trackNumber", type: "numeric", readOnly: !allowEdition },
    {
      data: "version",
      type: "dropdown",
      source: ["OV", "IV", "MusicAndEffects", "Dubbed", "AD", "Unused", "Undefined", "Commentary"],
      trimDropdown: false,
      readOnly: !allowEdition,
    },
    { data: "language.value", type: "dropdown", source: languagesValues, readOnly: !allowEdition },
    {
      data: "channelPosition",
      type: "dropdown",
      source: ["Lt", "Rt", "L", "R", "C", "LFE", "Ls", "Rs", "Lss", "Rss", "Lrs", "Rrs", "Lt_Rt", "Lt_Rt_C_LFE_Ls_Rs"],
      trimDropdown: false,
      readOnly: !allowEdition,
    },
    { data: "sampling", type: "numeric", readOnly: !allowEdition },
    { data: "bitDepth", type: "numeric", readOnly: !allowEdition },
    {
      data: "format",
      type: "dropdown",
      source: ["PCM", "DolbyE", "AAC", "AC3"],
      readOnly: !allowEdition,
      trimDropdown: false,
    },
    { data: "isLoudnessCorrected", type: "checkbox", readOnly: !allowEdition },
    { data: "isDolbyEEncoded", type: "checkbox", readOnly: !allowEdition },
  ];

  if (r128) {
    columns.push({ data: "loudnessValue", type: "numeric", readOnly: !allowEdition });
    columns.push({ data: "lra", type: "numeric", readOnly: !allowEdition });
    columns.push({ data: "truePeaks", type: "numeric", readOnly: !allowEdition });
  }

  const handleSave = () => {
    let update: IDbBaseMetadataValueInfos = {
      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAudioChannelsMetadataValueInfos, VDM.AirLab.Sdk.Core",
      id: metadata?.id!,
      fieldType: metadata?.fieldType!,
      creationTime: moment().toDate(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: ObjectLayer.Asset,
      valueLabel: null,
      value: audioChannels,
    };

    let request: UpdateMetadataFieldsRequest = {
      objectId: props.asset.id,
      metadata: [update],
    };

    assetService.UpdateMetadataFields(request).then((response) => {
      if (response && response.success) {
        enqueueCustomSnackbar(t("alert:success.audioChannelsUpated"), "success");
        props.onChange && props.onChange(request.metadata![0]);
      }
    });
  };

  return (
    <Box>
      <HotTable
        data={audioChannels}
        style={{ borderRadius: "4px" }}
        colHeaders={colHeaders}
        rowHeaders={false}
        columns={columns}
        height={audioChannels.length <= 4 && allowEdition ? "180" : "auto"} // We have an issue here, the auto height does not show the dropdown list
        stretchH="all"
        tableClassName="audiotracks-table"
        className="audiotracks-table"
        licenseKey="non-commercial-and-evaluation" // for non-commercial use only
      />

      {allowEdition && (
        <Button variant="outlined" sx={{ mt: 3 }} startIcon={<SaveIcon />} onClick={handleSave}>
          {t("button:saveChanges")}
        </Button>
      )}
    </Box>
  );
};

export default AudioTracksExcel;
